import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Search, Plus, Globe, Lock, UserPlus, MessageCircle, X, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { getMediaUrl } from '@/utils/mediaUtils'
import api from '@/services/api'

// ================================================================================
// TYPES
// ================================================================================
interface Group {
  id: number
  name: string
  description?: string
  coverImage?: string
  groupUuid?: string
  inviteToken?: string
  privacy?: string
  memberCount?: number
  postCount?: number
  admin?: { id: number; firstName: string; lastName: string }
  isAdmin?: boolean
  isMember?: boolean
  createdAt: string
}

// ================================================================================
// TOAST HELPER
// ================================================================================
const showToast = (message: string, type: 'success' | 'error' = 'success') => {
  const toast = document.createElement('div')
  toast.className = `fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-[9999] flex items-center gap-2 ${
    type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
  }`
  toast.innerHTML = `<span class="font-medium">${message}</span>`
  document.body.appendChild(toast)
  setTimeout(() => {
    toast.style.opacity = '0'
    toast.style.transition = 'opacity 0.3s'
    setTimeout(() => toast.remove(), 300)
  }, 3000)
}

// ================================================================================
// GROUP CARD COMPONENT
// ================================================================================
interface GroupCardProps {
  group: Group
  isMember: boolean
  onJoin: (id: number) => void
  onLeave: (id: number) => void
  onView: (group: Group) => void
}

const GroupCard = React.memo(({ group, isMember, onJoin, onLeave, onView }: GroupCardProps) => {
  const coverStyle = group.coverImage
    ? { backgroundImage: `url(${getMediaUrl(group.coverImage)})` }
    : {}

  return (
    <GovCard className="overflow-hidden p-0">
      {/* Cover */}
      <div
        className="h-28 bg-gradient-to-br from-blue-500 to-cyan-500 relative"
        style={coverStyle}
      >
        {!group.coverImage && (
          <div className="w-full h-full flex items-center justify-center">
            <Users className="w-10 h-10 text-white/40" />
          </div>
        )}
        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
          <Users className="w-3 h-3" />
          {group.memberCount || 0}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-base mb-1 text-gray-900">{group.name}</h3>
        {group.description && (
          <p className="text-gray-500 text-sm mb-3 line-clamp-2">{group.description}</p>
        )}

        {/* Privacy Badge */}
        <div className="mb-3">
          {group.privacy === 'PRIVATE' ? (
            <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
              <Lock className="w-3 h-3" /> Private
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
              <Globe className="w-3 h-3" /> Public
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {isMember ? (
            <>
              <GovButton variant="secondary" size="sm" className="flex-1" onClick={() => onView(group)}>
                View
              </GovButton>
              <GovButton variant="ghost" size="sm" className="flex-1 text-red-600 hover:text-red-700" onClick={() => onLeave(group.id)}>
                Leave
              </GovButton>
            </>
          ) : (
            <GovButton variant="primary" size="sm" className="flex-1" onClick={() => onJoin(group.id)}>
              <UserPlus className="w-4 h-4 inline mr-1" /> Join
            </GovButton>
          )}
        </div>
      </div>
    </GovCard>
  )
})

GroupCard.displayName = 'GroupCard'

// ================================================================================
// CREATE GROUP MODAL
// ================================================================================
interface CreateGroupModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { name: string; description: string; privacy: string }) => void
  isLoading: boolean
}

const CreateGroupModal = React.memo(({ isOpen, onClose, onSubmit, isLoading }: CreateGroupModalProps) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacy: 'PUBLIC'
  })

  const handleSubmit = useCallback(() => {
    if (!formData.name.trim()) return
    onSubmit(formData)
  }, [formData, onSubmit])

  const handleClose = useCallback(() => {
    setFormData({ name: '', description: '', privacy: 'PUBLIC' })
    onClose()
  }, [onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Create New Group</h2>
          <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Group Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Enter group name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-gray-700">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none resize-none"
              rows={3}
              placeholder="What is this group about?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Privacy</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="privacy"
                  value="PUBLIC"
                  checked={formData.privacy === 'PUBLIC'}
                  onChange={e => setFormData({ ...formData, privacy: e.target.value })}
                  className="text-blue-600"
                />
                <Globe className="w-4 h-4 text-gray-500" />
                <span className="text-sm">Public</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="privacy"
                  value="PRIVATE"
                  checked={formData.privacy === 'PRIVATE'}
                  onChange={e => setFormData({ ...formData, privacy: e.target.value })}
                  className="text-blue-600"
                />
                <Lock className="w-4 h-4 text-gray-500" />
                <span className="text-sm">Private</span>
              </label>
            </div>
          </div>

          <GovButton
            variant="primary"
            className="w-full"
            onClick={handleSubmit}
            disabled={!formData.name.trim() || isLoading}
          >
            {isLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Create Group'}
          </GovButton>
        </div>
      </div>
    </div>
  )
})

CreateGroupModal.displayName = 'CreateGroupModal'

// ================================================================================
// GROUP DETAIL MODAL
// ================================================================================
interface GroupDetailModalProps {
  group: Group | null
  onClose: () => void
  onChat: (group: Group) => void
  onLeave: (id: number) => void
}

const GroupDetailModal = React.memo(({ group, onClose, onChat, onLeave }: GroupDetailModalProps) => {
  if (!group) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Cover */}
        <div className="h-36 bg-gradient-to-br from-blue-500 to-cyan-500 relative">
          {group.coverImage ? (
            <img src={getMediaUrl(group.coverImage)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Users className="w-14 h-14 text-white/40" />
            </div>
          )}
          <button onClick={onClose} className="absolute top-3 right-3 p-2 bg-black/50 rounded-full text-white hover:bg-black/70">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          <h2 className="text-xl font-bold mb-1 text-gray-900">{group.name}</h2>
          <p className="text-gray-500 text-sm mb-4">{group.description}</p>

          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" /> {group.memberCount || 0} members
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="w-4 h-4" /> {group.postCount || 0} posts
            </span>
          </div>

          <div className="flex gap-2">
            <GovButton variant="primary" onClick={() => onChat(group)}>
              <MessageCircle className="w-4 h-4 mr-1" /> Chat
            </GovButton>
            <GovButton variant="secondary" className="text-red-600 hover:text-red-700" onClick={() => onLeave(group.id)}>
              Leave Group
            </GovButton>
          </div>
        </div>
      </div>
    </div>
  )
})

GroupDetailModal.displayName = 'GroupDetailModal'

// ================================================================================
// MAIN GROUPS PAGE
// ================================================================================
const GroupsPage = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [publicGroups, setPublicGroups] = useState<Group[]>([])
  const [myGroups, setMyGroups] = useState<Group[]>([])
  const [activeTab, setActiveTab] = useState<'my' | 'discover'>('my')
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showGroupDetail, setShowGroupDetail] = useState<Group | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    fetchGroups()
  }, [])

  const fetchGroups = useCallback(async () => {
    setLoading(true)
    try {
      const [publicRes, myRes] = await Promise.all([
        api.get('/groups/public?page=0&size=50'),
        isAuthenticated ? api.get('/groups/my?page=0&size=50') : Promise.resolve({ data: { success: true, data: { content: [] } } })
      ])

      if (publicRes.data.success) {
        setPublicGroups(publicRes.data.data?.content || [])
      }
      if (myRes.data.success && isAuthenticated) {
        setMyGroups(myRes.data.data?.content || [])
      }
    } catch (error) {
      console.error('Error fetching groups:', error)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const joinGroup = useCallback(async (groupId: number) => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    try {
      const response = await api.post(`/groups/${groupId}/join`)
      if (response.data.success) {
        showToast('Joined group successfully', 'success')
        fetchGroups()
      }
    } catch (error: any) {
      console.error('Error joining group:', error)
      const message = error.response?.data?.message || 'Failed to join group'
      showToast(message, 'error')
    }
  }, [isAuthenticated, navigate, fetchGroups])

  const leaveGroup = useCallback(async (groupId: number) => {
    try {
      const response = await api.post(`/groups/${groupId}/leave`)
      if (response.data.success) {
        showToast('Left group successfully', 'success')
        setShowGroupDetail(null)
        fetchGroups()
      }
    } catch (error: any) {
      console.error('Leave group error:', error)
      const message = error.response?.data?.message || error.message || 'Failed to leave group'
      showToast(message, 'error')
    }
  }, [fetchGroups])

  const createGroup = useCallback(async (data: { name: string; description: string; privacy: string }) => {
    if (!data.name.trim()) return
    setCreating(true)
    try {
      const response = await api.post('/groups', data)
      if (response.data.success) {
        setShowCreateModal(false)
        showToast('Group created successfully', 'success')
        fetchGroups()
      }
    } catch (error: any) {
      console.error('Error creating group:', error)
      const message = error.response?.data?.message || 'Failed to create group'
      showToast(message, 'error')
    } finally {
      setCreating(false)
    }
  }, [fetchGroups])

  const filteredGroups = useMemo(() => {
    const groups = activeTab === 'my' ? myGroups : publicGroups
    return groups.filter(g =>
      g.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      g.description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [activeTab, myGroups, publicGroups, searchQuery])

  const isMemberOfGroup = useCallback((groupId: number) => {
    return myGroups.some(g => g.id === groupId)
  }, [myGroups])

  const handleViewGroup = useCallback((group: Group) => {
    setShowGroupDetail(group)
  }, [])

  // Clicking "Chat" on a joined community group -> get-or-create the GROUP conversation
  // linked to it (backend seeds it with the group's members), then open it in Messages.
  const handleChat = useCallback(async (group: Group) => {
    try {
      const res = await api.post(`/messages/conversation/group-by-community/${group.id}`)
      const conv = res.data?.data
      navigate('/messages', { state: { openConversationId: conv?.id, type: conv?.type } })
    } catch (error: any) {
      console.error('Error opening group chat:', error)
      const message = error?.response?.data?.message || 'Failed to open group chat'
      showToast(message, 'error')
      // Still take the user to messages so they're not stuck on the modal
      navigate('/messages')
    }
    setShowGroupDetail(null)
  }, [navigate])

  return (
    <GovLayout>
      <GovPageTitle title="Groups" subtitle="Connect with TB awareness communities" icon={Users} />

      {/* Header Actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('my')}
            className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
              activeTab === 'my' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Users className="w-4 h-4 inline mr-1" /> My Groups
          </button>
          <button
            onClick={() => setActiveTab('discover')}
            className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
              activeTab === 'discover' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Globe className="w-4 h-4 inline mr-1" /> Discover
          </button>
        </div>

        {isAuthenticated && (
          <GovButton variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-1" /> Create
          </GovButton>
        )}
      </div>

      {/* Search */}
      <GovCard className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border-0 focus:ring-0 outline-none bg-transparent"
          />
        </div>
      </GovCard>

      {/* Groups Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-500" />
          <p className="mt-2 text-gray-500">Loading groups...</p>
        </div>
      ) : filteredGroups.length === 0 ? (
        <GovEmptyState
          icon={activeTab === 'my' ? '👥' : '🔍'}
          title={activeTab === 'my' ? 'No groups joined yet' : 'No groups found'}
          description={activeTab === 'my' ? 'Discover and join groups to connect with others' : 'Try a different search term'}
          action={activeTab === 'my' ? { label: 'Discover Groups', onClick: () => setActiveTab('discover') } : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredGroups.map(group => (
            <GroupCard
              key={group.id}
              group={group}
              isMember={isMemberOfGroup(group.id)}
              onJoin={joinGroup}
              onLeave={leaveGroup}
              onView={handleViewGroup}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      <CreateGroupModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={createGroup}
        isLoading={creating}
      />

      <GroupDetailModal
        group={showGroupDetail}
        onClose={() => setShowGroupDetail(null)}
        onChat={handleChat}
        onLeave={leaveGroup}
      />
    </GovLayout>
  )
}

export default React.memo(GroupsPage)